import type { VariantProps } from 'class-variance-authority';

import { cnva } from '@/app/utils/cnva';

const DividerStyled = cnva('pointer-events-none', {
  variants: {
    theme: {
      light: '',
      dark: '',
    },
    orientation: {
      horizontal: 'w-full',
      vertical: 'self-stretch border-l-1',
    },
    size: {
      none: '',
      sm: '',
      md: '',
      lg: '',
      xl: '',
      '2xl': '',
    },
    color: {
      'light-gray': '',
      gray: '',
      'dark-gray': '',
    },
  },
  compoundVariants: [
    {
      theme: 'light',
      color: 'light-gray',
      className: 'border-gray-50',
    },
    {
      theme: 'light',
      color: 'gray',
      className: 'border-gray-300',
    },
    {
      theme: 'light',
      color: 'dark-gray',
      className: 'border-gray-900',
    },
    {
      theme: 'dark',
      color: 'light-gray',
      className: 'border-gray-800',
    },
    {
      theme: 'dark',
      color: 'gray',
      className: 'border-gray-500',
    },
    {
      theme: 'dark',
      color: 'dark-gray',
      className: 'border-gray-50',
    },
    {
      orientation: 'horizontal',
      size: 'sm',
      className: 'my-2',
    },
    {
      orientation: 'horizontal',
      size: 'md',
      className: 'my-4',
    },
    {
      orientation: 'horizontal',
      size: 'lg',
      className: 'my-6',
    },
    {
      orientation: 'horizontal',
      size: 'xl',
      className: 'my-8',
    },
    {
      orientation: 'horizontal',
      size: '2xl',
      className: 'my-16',
    },
    {
      orientation: 'vertical',
      size: 'sm',
      className: 'mx-2',
    },
    {
      orientation: 'vertical',
      size: 'md',
      className: 'mx-4',
    },
    {
      orientation: 'vertical',
      size: 'lg',
      className: 'mx-6',
    },
    {
      orientation: 'vertical',
      size: 'xl',
      className: 'mx-8',
    },
    {
      orientation: 'vertical',
      size: '2xl',
      className: 'mx-16',
    },
  ],
  defaultVariants: {
    theme: 'light',
    orientation: 'horizontal',
    size: 'md',
    color: 'light-gray',
  },
});

export interface DividerProps {
  /**
   * (Optional) The class name for the divider
   */
  className?: string;

  /**
   * (Optional) The theme of the divider
   */
  theme?: VariantProps<typeof DividerStyled>['theme'];

  /**
   * (Optional) The orientation of the divider
   */
  orientation?: VariantProps<typeof DividerStyled>['orientation'];

  /**
   * (Optional) The color of the divider
   */
  color?: VariantProps<typeof DividerStyled>['color'];

  /**
   * (Optional) The size of the divider (size is applied as margin)
   */
  size?: VariantProps<typeof DividerStyled>['size'];
}

export const Divider = ({
  className,
  theme = 'light',
  orientation = 'horizontal',
  color = 'light-gray',
  size = 'md',
}: DividerProps) => {
  const Component = orientation === 'vertical' ? 'div' : 'hr';

  return (
    <Component
      {...(orientation === 'vertical' && {
        role: 'separator',
        ['aria-orientation']: orientation,
      })}
      className={DividerStyled({
        theme,
        orientation,
        color,
        size,
        className,
      })}
    />
  );
};
